import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfigureTab from "@/components/assistentes/tabs/ConfigureTab";
import PromptTab from "@/components/assistentes/tabs/PromptTab";
import KnowledgeBaseTab from "@/components/assistentes/tabs/KnowledgeBaseTab";
import IntegrationsTab from "@/components/assistentes/tabs/IntegrationsTab/IntegrationsTab";
import WebhooksTab from "@/components/assistentes/tabs/WebhooksTab";
import AssistantSimulationChat from "@/components/assistentes/AssistantSimulationChat";
import { Button } from "@/components/ui/button";
import { useAssistants } from "@/hooks/useAssistants";

const AssistenteEditar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assistants, updateAssistant } = useAssistants();

  const [activeTab, setActiveTab] = useState("configure");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const assistant = assistants.find(a => a.id === id);

  const [formData, setFormData] = useState({
    name: '',
    model: 'gpt-5',
    response_time_value: 1,
    response_time_unit: 'minutes',
    max_messages: 1,
    prompt_mode: 'guided-mode',
    default_prompt: '',
    voice_tone: '',
    main_objective: '',
    language_rules: '',
    good_examples: '',
    restrictions: '',
    additional_info: '',
    is_active: true
  });

  useEffect(() => {
    if (assistant) {
      setFormData({
        name: assistant.name,
        model: assistant.model,
        response_time_value: assistant.response_time_value,
        response_time_unit: assistant.response_time_unit,
        max_messages: assistant.max_messages,
        prompt_mode: assistant.prompt_mode,
        default_prompt: assistant.default_prompt || '',
        voice_tone: assistant.voice_tone || '',
        main_objective: assistant.main_objective || '',
        language_rules: assistant.language_rules || '',
        good_examples: assistant.good_examples || '',
        restrictions: assistant.restrictions || '',
        additional_info: assistant.additional_info || '',
        is_active: assistant.is_active
      });
    }
  }, [assistant]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Nome do assistente é obrigatório' });
      return;
    }

    if (!id) return;

    setLoading(true);
    setMessage(null);

    try {
      const success = await updateAssistant(id, formData);

      if (success) {
        setMessage({ type: 'success', text: 'Assistente atualizado com sucesso!' });
        setTimeout(() => {
          setMessage(null);
        }, 3000);
      } else {
        setMessage({ type: 'error', text: 'Falha ao atualizar assistente' });
      }
    } catch (error) {
      console.error('Erro ao atualizar assistente:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar assistente' });
    } finally {
      setLoading(false);
    }
  };

  if (!assistant) {
    return (
      <div className="w-full h-[calc(100dvh-110px)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Assistente não encontrado</h3>
          <button
            onClick={() => navigate('/assistentes')}
            className="mt-4 px-4 py-2 bg-neutral-600 hover:bg-neutral-500 rounded-lg text-neutral-50"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100dvh-110px)] flex flex-col">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/assistentes')}
          className="flex items-center gap-2 text-neutral-50 hover:text-neutral-300 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h2 className="text-[26px] sm:text-[30px] font-semibold text-neutral-50">
          Editar Assistente: {assistant.name}
        </h2>
      </div>

      {message && (
        <div className={`p-3 rounded-md mb-4 ${
          message.type === 'success'
            ? 'bg-green-500/20 border border-green-500/30 text-green-300'
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 flex flex-col">
          <div className="flex-1 min-h-0 border-[1px] border-[#3F3F464D] rounded-[12px] bg-[#18181B] p-4 overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="mb-4">
                <TabsTrigger value="configure">Configure</TabsTrigger>
                <TabsTrigger value="prompt">Prompt</TabsTrigger>
                <TabsTrigger value="knowledge-base">Base de conhecimento</TabsTrigger>
                <TabsTrigger value="integrations">Integrações</TabsTrigger>
                <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto">
                <TabsContent value="configure" className="mt-0">
                  <ConfigureTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="prompt" className="mt-0">
                  <PromptTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="knowledge-base" className="mt-0">
                  <KnowledgeBaseTab />
                </TabsContent>
                <TabsContent value="integrations" className="mt-0">
                  {id && <IntegrationsTab assistantId={id} />}
                </TabsContent>
                <TabsContent value="webhooks" className="mt-0">
                  <WebhooksTab />
                </TabsContent>
              </div>
            </Tabs>

            {(activeTab === "configure" || activeTab === "prompt") && (
              <div className="mt-4 pt-4 border-t border-[#3F3F464D]">
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-[120px]"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 h-full min-h-[500px] lg:min-h-0">
          <AssistantSimulationChat assistant={{
            ...assistant,
            ...formData
          }} />
        </div>
      </div>
    </div>
  );
};

export default AssistenteEditar;
